import React, { useMemo, useState } from "react";
import { Bot, Mail, MessageSquare, Search, Shield, Wrench, Zap } from "lucide-react";

import { PublicLayout } from "@/components/layout/PublicLayout";
import { useTranslation } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const FAQS = [
  { q: "Cloudface là gì?", a: "Cloudface là nền tảng điều phối agent AI cho doanh nghiệp, giúp triển khai, quản trị và giám sát các agent chuyên biệt." },
  { q: "Chi phí được tính như thế nào?", a: "Hệ thống hỗ trợ mô hình thuê bao kết hợp usage-based billing theo token hoặc theo lượt gọi API." },
  { q: "Dữ liệu của tôi có bị dùng để huấn luyện không?", a: "Không. Dữ liệu doanh nghiệp xử lý qua Cloudface không được dùng để huấn luyện mô hình." },
  { q: "Có hỗ trợ 2FA không?", a: "Có. Hệ thống hỗ trợ OTP qua email và có thể mở rộng sang khóa bảo mật phần cứng." },
  { q: "Tôi có thể tự host tool riêng không?", a: "Có. Bạn có thể kết nối REST API riêng hoặc xây pipeline nội bộ qua Tools Studio." },
  { q: "Cloudface lưu dữ liệu ở đâu?", a: "Nền tảng hỗ trợ các vùng lưu trữ gần Việt Nam và Đông Nam Á để tối ưu độ trễ." },
  { q: "Mời thành viên mới bằng cách nào?", a: "Vào phần Settings > Team để mời thành viên và gán quyền phù hợp." },
];

export default function HelpPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const filteredFaqs = useMemo(
    () => FAQS.filter((faq) => faq.q.toLowerCase().includes(search.toLowerCase()) || faq.a.toLowerCase().includes(search.toLowerCase())),
    [search],
  );

  return (
    <PublicLayout>
      <div className="space-y-12">
        <section className="space-y-6 text-center">
          <h1 className="text-4xl font-bold tracking-tight">{t("help_title")}</h1>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Tìm nhanh câu trả lời, hướng dẫn thao tác và các điểm cần biết để vận hành Cloudface ổn định hơn.
          </p>
          <div className="relative mx-auto max-w-xl">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Tìm bài viết trợ giúp..." className="h-12 bg-card/50 pl-10" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
        </section>

        <div className="grid gap-6 md:grid-cols-4">
          <QuickGuideIcon icon={Bot} title="Agents" description="Quản lý vai trò, prompt và quyền của agent." />
          <QuickGuideIcon icon={Wrench} title="Tools" description="Kết nối API, schema và quyền truy cập." />
          <QuickGuideIcon icon={Shield} title="Security" description="Bảo mật, masking và kiểm soát truy cập." />
          <QuickGuideIcon icon={Zap} title="Integrations" description="Tối ưu kết nối và xử lý cảnh báo vận hành." />
        </div>

        <section className="space-y-6">
          <h2 className="text-2xl font-bold">{t("frequently_asked_questions")}</h2>
          <Accordion type="single" collapsible className="w-full">
            {filteredFaqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`}>
                <AccordionTrigger className="text-left font-medium">{faq.q}</AccordionTrigger>
                <AccordionContent className="leading-relaxed text-muted-foreground">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="flex flex-col items-center justify-between gap-8 rounded-3xl border border-primary/20 bg-primary/5 p-8 md:flex-row">
          <div className="space-y-2">
            <h3 className="text-xl font-bold">Cần hỗ trợ thêm?</h3>
            <p className="text-muted-foreground">Bạn có thể liên hệ đội hỗ trợ hoặc mở kênh trao đổi nhanh với nhóm vận hành.</p>
          </div>
          <div className="flex gap-4">
            <Button className="gap-2" asChild>
              <a href="mailto:support@cloudface.vn?subject=Ho%20tro%20Cloudface">
                <Mail className="h-4 w-4" /> Email Support
              </a>
            </Button>
            <Button variant="outline" className="gap-2" asChild>
              <a href="https://slack.com" target="_blank" rel="noreferrer">
                <MessageSquare className="h-4 w-4" /> Slack Community
              </a>
            </Button>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}

function QuickGuideIcon({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <Card className="cursor-pointer transition-colors hover:border-primary group">
      <CardContent className="flex flex-col items-center gap-2 p-6 text-center">
        <Icon className="h-8 w-8 text-muted-foreground transition-colors group-hover:text-primary" />
        <span className="font-semibold">{title}</span>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}
